import { FBRef, GQL } from "@binale-tech/shared";
import { database } from "@domain/api/firebase/firebase";
import { child, ref, type get } from "firebase/database";

export type Reference = ReturnType<typeof ref>;
export type DataSnapshot = Awaited<ReturnType<typeof get>>;

// Unified record root. Full per-record path:
//   records/{FBRef.Records_*_V2}/{company_id}/{group_id|"default"}/{year}/{record_id}
// Replaces the prior 8 top-level per-product roots (refRecordsER, refRecordsDeb, …)
// with a single subtree, but preserves the existing `FBRef.Records_*_V2` values as
// the product segment so the migration is a single re-key of the parent path.
// Listeners and one-shot fetches resolve via `getPKRecordsRef`.
export const refRecords = ref(database, "records");

const RECORD_PRODUCT_TO_FBREF: Partial<Record<GQL.IProductKey, FBRef>> = {
    [GQL.IProductKey.Er]: FBRef.Records_ER_V2,
    [GQL.IProductKey.ErA]: FBRef.Records_ER_A_V2,
    [GQL.IProductKey.Deb]: FBRef.Records_Deb_V2,
    [GQL.IProductKey.Fe]: FBRef.Records_FE_V2,
    [GQL.IProductKey.La]: FBRef.Records_LA_V2,
    [GQL.IProductKey.Pos]: FBRef.Records_Pos_V2,
    [GQL.IProductKey.Bank]: FBRef.Records_Bank_V2,
    [GQL.IProductKey.Kb]: FBRef.Records_KB_V2,
};

export const getPKRecordsRef = (productKey: GQL.IProductKey): Reference => {
    const segment = RECORD_PRODUCT_TO_FBREF[productKey];
    if (!segment) {
        throw new Error(`No Firebase records segment defined for product ${productKey}`);
    }
    return child(refRecords, segment);
};

export const refPayments = ref(database, FBRef.Payments);

export const refCreditor = ref(database, FBRef.Creditors);
export const refDebitor = ref(database, FBRef.Debitors);
export const refCategory = ref(database, FBRef.Categories);
export const refTag = ref(database, FBRef.Tags);

export const refUsersSessions = ref(database, FBRef.UsersSessions);
export const refContacts = ref(database, FBRef.Contacts);
export const refDocuments = ref(database, FBRef.Documents);
export const refSettings = ref(database, FBRef.Settings);
export const refBanks = ref(database, FBRef.Banks);
export const refCashRegisters = ref(database, FBRef.CashRegisters);
export const refTemplates = ref(database, FBRef.Templates);
export const refInvoices = ref(database, FBRef.Invoices);
export const refRecordTextTemplates = ref(database, FBRef.RecordTextTemplates);

// Realtime inversion of record→documents - `{Associations}/recordDocuments/{companyId}/{recordId}: [docId, ...]`.
// Maintained by the backend (RecordDocumentAssociationsFBRepository).
export const refRecordDocumentAssociations = ref(database, `${FBRef.Associations}/recordDocuments`);

// Same shape, but for banking and cash register transactions.
export const refBankTransactionDocumentAssociations = ref(database, `${FBRef.Associations}/bankTransactionDocuments`);
export const refCashRegisterTransactionDocumentAssociations = ref(
    database,
    `${FBRef.Associations}/cashRegisterTransactionDocuments`,
);
